/**
 * MediaUploadSection Component
 *
 * Complete media upload section with images and videos.
 * Matches the design reference with grid layout for images and list for videos.
 * Supports real file uploads with preview.
 */

import {
  ArrowUpToLine,
  GripVertical,
  Image,
  Music,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Video,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { CustomInput } from "@/components/forms/customInput"
import { fetchVideoMetadata, isValidVideoUrl } from "@/utils/videoThumbnail"
import { AiImageGenerator, type EntityContext } from "./AiImageGenerator"
import { MediaUploadModal } from "./MediaUploadModal"
import { MediaPreview } from "./mediaPreview"

interface ImageItem {
  id: string
  src?: string
  file?: File
  isCover: boolean
  aiGenerationImageIndex?: number
  uploadId?: string
}

interface VideoItem {
  id: string
  title: string
  url: string
  thumbnail?: string
  file?: File
}

interface AudioItem {
  id: string
  name: string
  sizeBytes: number
  file?: File
  uploadId?: string
  // Optional cover image for the audio. When set, doubles as the hero
  // background and the course-list row thumbnail. posterFile is the new
  // upload from the user; posterUrl is either a local blob URL preview or
  // a server-side URL (from a previously saved ritual).
  posterFile?: File
  posterUrl?: string
}

interface VideoUrlEntry {
  id: string
  url: string
  thumbnail: string
  isEditing?: boolean
  title?: string
  isProcessed?: boolean // Track if metadata was attempted to fetch
  isInvalid?: boolean // Track if URL is invalid (non-YouTube/non-Vimeo)
}

interface MediaUploadSectionProps {
  onMediaChange?: (media: {
    images: Array<{ id: string; file?: File; src?: string; isCover: boolean; uploadId?: string }>
    videos: Array<{ id: string; file?: File; url?: string }>
    audios?: Array<{
      id: string
      file?: File
      name: string
      sizeBytes: number
      uploadId?: string
      posterFile?: File
      posterUrl?: string
    }>
    aiGenerationId?: string | null
    aiGenerationImageIndex?: number | null
  }) => void
  onVideoUrlsChange?: (videos: VideoUrlEntry[]) => void
  onVideoThumbnailUpload?: (videoId: string, file: File) => Promise<void> // Callback for thumbnail upload
  initialImages?: Array<{
    id: string
    file?: File
    src?: string
    isCover: boolean
    uploadId?: string
  }>
  initialVideos?: Array<{ id: string; file?: File; url?: string }>
  initialAudios?: Array<{
    id: string
    file?: File
    name: string
    sizeBytes: number
    uploadId?: string
    posterFile?: File
    posterUrl?: string
  }>
  initialVideoUrls?: VideoUrlEntry[]
  showVideoSection?: boolean // Default: true
  showVideoUrlInput?: boolean // Default: false
  showAudioSection?: boolean // Default: false
  maxFiles?: number // Default: 10
  maxFileSize?: number // Default: 5MB (5 * 1024 * 1024)
  maxAudioFileSize?: number // Default: 100MB
  allowedTypes?: string[] // Default: ['image/png', 'image/jpeg', 'image/jpg']
  enableAiGeneration?: boolean // Default: false
  aiEntityType?: "ritual" | "community" | "coaching"
  aiEntityContext?: EntityContext
  communityId?: string // Community ID for thumbnail upload (optional)
}

// Default values
const DEFAULT_MAX_FILES = 10
const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const DEFAULT_MAX_AUDIO_FILE_SIZE = 100 * 1024 * 1024 // 100MB
const DEFAULT_ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"]

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`
  return `${bytes}B`
}

export function MediaUploadSection({
  onMediaChange,
  onVideoUrlsChange,
  onVideoThumbnailUpload,
  initialImages,
  initialVideos,
  initialAudios,
  initialVideoUrls = [],
  showVideoSection = true,
  showVideoUrlInput = false,
  showAudioSection = false,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxAudioFileSize = DEFAULT_MAX_AUDIO_FILE_SIZE,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  enableAiGeneration = false,
  aiEntityType,
  aiEntityContext,
  communityId,
}: MediaUploadSectionProps) {
  const [images, setImages] = useState<ImageItem[]>([])
  const [videos, setVideos] = useState<VideoItem[]>([])
  const [audios, setAudios] = useState<AudioItem[]>(
    initialAudios?.map(a => ({
      id: a.id,
      name: a.name,
      sizeBytes: a.sizeBytes,
      file: a.file,
      uploadId: a.uploadId,
      posterFile: a.posterFile,
      posterUrl: a.posterUrl,
    })) ?? [],
  )
  const audioPosterInputRef = useRef<HTMLInputElement>(null)
  const [videoUrls, setVideoUrls] = useState<VideoUrlEntry[]>(
    initialVideoUrls.length > 0
      ? initialVideoUrls
      : [{ id: "video-0", url: "", thumbnail: "", title: "", isProcessed: false }],
  )
  const [urlErrors, setUrlErrors] = useState<Record<number, string | null>>({})
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [uploadingThumbnailIndex, setUploadingThumbnailIndex] = useState<number | null>(null)
  const [thumbnailError, setThumbnailError] = useState<string | null>(null)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const thumbnailInputRef = useRef<HTMLInputElement>(null)
  const latestVideoUrlsRef = useRef<VideoUrlEntry[]>(videoUrls)

  // Debounce timers for video thumbnail generation
  const debounceTimersRef = useRef<Record<number, NodeJS.Timeout>>({})

  // Focus input when entering edit mode
  useEffect(() => {
    videoUrls.forEach((video, index) => {
      if (video.isEditing) {
        const input = document.getElementById(`videoUrl-${index}`)
        if (input) {
          input.focus()
        }
      }
    })
  }, [videoUrls])

  // Handle video URL change at specific index
  const handleVideoUrlChange = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newUrl = e.target.value

    // Clear existing timer for this index
    if (debounceTimersRef.current[index]) {
      clearTimeout(debounceTimersRef.current[index])
    }

    // Update URL immediately
    setVideoUrls(prev => {
      const updated = [...prev]
      if (updated[index]) {
        updated[index] = { ...updated[index], url: newUrl }
      }
      return updated
    })

    // Validate URL - show error if not empty and not a valid video URL
    if (newUrl && !isValidVideoUrl(newUrl)) {
      setUrlErrors(prev => ({ ...prev, [index]: "Please enter a valid YouTube or Vimeo URL" }))

      // Mark as invalid to keep input visible and prevent useEffect from fetching
      setVideoUrls(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = { ...updated[index], isInvalid: true, isProcessed: true }
        }
        return updated
      })
      return
    }

    // Clear error if URL is empty or valid
    if (urlErrors[index]) {
      setUrlErrors(prev => ({ ...prev, [index]: null }))
    }

    // Clear invalid flag and reset isProcessed when URL changes (to allow re-fetching)
    if (!newUrl || isValidVideoUrl(newUrl)) {
      setVideoUrls(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = { ...updated[index], isInvalid: false, isProcessed: false }
        }
        return updated
      })
    }

    // Only fetch metadata if URL is valid
    if (isValidVideoUrl(newUrl)) {
      // Mark as processed and debounce metadata fetching by 500ms
      setVideoUrls(prev => {
        const updated = [...prev]
        if (updated[index]) {
          updated[index] = { ...updated[index], isProcessed: true }
        }
        return updated
      })

      debounceTimersRef.current[index] = setTimeout(async () => {
        const { thumbnail, title } = await fetchVideoMetadata(newUrl)

        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail, title }
          }
          // Notify parent with updated data
          if (onVideoUrlsChange) {
            onVideoUrlsChange(updated.filter(v => v.url))
          }
          return updated
        })
      }, 500)
    }
  }

  // Add more video URL
  const addVideoUrl = () => {
    const newIndex = videoUrls.length
    setVideoUrls(prev => [
      ...prev,
      { id: `new-${newIndex}`, url: "", thumbnail: "", title: "", isProcessed: false },
    ])
  }

  // Delete video URL
  const handleDeleteVideo = (index: number) => {
    // Don't allow deleting first empty input
    if (index === 0 && !videoUrls[0]?.url) {
      return
    }

    const updated = videoUrls.filter((_, i) => i !== index)
    setVideoUrls(updated)

    // Clean up urlErrors
    const newErrors: Record<number, string | null> = {}
    updated.forEach((_, i) => {
      if (urlErrors[i] !== undefined) {
        newErrors[i] = urlErrors[i]
      }
    })
    setUrlErrors(newErrors)

    // Notify parent
    if (onVideoUrlsChange) {
      onVideoUrlsChange(updated.filter(v => v.url))
    }
  }

  // Edit video URL - switch to edit mode
  const handleEditVideo = (index: number) => {
    setVideoUrls(prev =>
      prev.map((video, i) => (i === index ? { ...video, isEditing: true } : video)),
    )
  }

  // Finish editing video URL - refetch metadata
  const handleFinishEditVideo = async (index: number) => {
    const video = videoUrls[index]
    if (!video?.url) return

    // Clear any pending debounce timer
    if (debounceTimersRef.current[index]) {
      clearTimeout(debounceTimersRef.current[index])
    }

    // Mark as processed and fetch metadata immediately
    const { thumbnail, title } = await fetchVideoMetadata(video.url)

    setVideoUrls(prev => {
      const updated = prev.map((v, i) =>
        i === index ? { ...v, thumbnail, title, isEditing: false, isProcessed: true } : v,
      )
      // Notify parent with updated data
      if (onVideoUrlsChange) {
        onVideoUrlsChange(updated.filter(v => v.url))
      }
      return updated
    })
  }

  // Handle blur on video URL input - finish editing
  const handleVideoUrlBlur = (index: number) => {
    handleFinishEditVideo(index)
  }

  // Drag and drop handlers for video reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newVideoUrls = [...videoUrls]
    const draggedItem = newVideoUrls[draggedIndex]
    newVideoUrls.splice(draggedIndex, 1)
    newVideoUrls.splice(index, 0, draggedItem)

    setVideoUrls(newVideoUrls)
    setDraggedIndex(index)

    // Notify parent with updated order
    if (onVideoUrlsChange) {
      onVideoUrlsChange(newVideoUrls.filter(v => v.url))
    }
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  // Handle thumbnail file selection
  const handleThumbnailSelect = (index: number) => {
    thumbnailInputRef.current?.click()
    // Store the index of the video for which thumbnail is being uploaded
    if (thumbnailInputRef.current) {
      ;(
        thumbnailInputRef.current as HTMLInputElement & { dataset: { videoIndex?: string } }
      ).dataset.videoIndex = index.toString()
    }
  }

  // Handle thumbnail file change
  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const index = parseInt(
      (e.target as HTMLInputElement & { dataset: { videoIndex?: string } }).dataset.videoIndex ||
        "0",
      10,
    )
    const video = videoUrls[index]

    // Validate file type (images only)
    if (!file.type.startsWith("image/")) {
      setThumbnailError("Please select an image file (JPEG, PNG, GIF, WebP)")
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setThumbnailError("Thumbnail image must be less than 5MB")
      return
    }

    setThumbnailError(null)
    setUploadingThumbnailIndex(index)

    try {
      if (onVideoThumbnailUpload && video.id && communityId) {
        // Use the provided callback to upload thumbnail (for existing videos)
        await onVideoThumbnailUpload(video.id, file)
      } else {
        // For new videos, create a local preview URL
        const localThumbnailUrl = URL.createObjectURL(file)
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail: localThumbnailUrl }
          }
          // Notify parent with updated data
          if (onVideoUrlsChange) {
            onVideoUrlsChange(updated.filter(v => v.url))
          }
          return updated
        })
      }
    } catch (error) {
      setThumbnailError("Failed to upload thumbnail. Please try again.")
      console.error("Thumbnail upload error:", error)
    } finally {
      setUploadingThumbnailIndex(null)
    }

    // Reset input
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = ""
    }
  }

  // Sync initial video URLs when prop changes (e.g., pre-fill from duplicate source after async load)
  useEffect(() => {
    if (initialVideoUrls && initialVideoUrls.length > 0) {
      const hasChanged =
        JSON.stringify(initialVideoUrls) !== JSON.stringify(latestVideoUrlsRef.current)
      if (hasChanged) {
        setVideoUrls(initialVideoUrls)
      }
    }
  }, [initialVideoUrls])

  // Fetch metadata for URLs that don't have thumbnails yet
  const fetchedMetadataRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    videoUrls.forEach(async (video, index) => {
      // Skip if already fetched, has thumbnail, URL is empty, or URL is invalid
      if (
        !video.url ||
        video.thumbnail ||
        fetchedMetadataRef.current.has(video.id) ||
        video.isInvalid
      ) {
        return
      }

      // Only fetch if URL is valid
      if (isValidVideoUrl(video.url)) {
        fetchedMetadataRef.current.add(video.id)

        // Mark as processed
        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], isProcessed: true }
          }
          return updated
        })

        const { thumbnail, title } = await fetchVideoMetadata(video.url)

        setVideoUrls(prev => {
          const updated = [...prev]
          if (updated[index]) {
            updated[index] = { ...updated[index], thumbnail, title }
          }
          // Notify parent with updated data
          if (onVideoUrlsChange) {
            onVideoUrlsChange(updated.filter(v => v.url))
          }
          return updated
        })
      }
    })
  }, [videoUrls, onVideoUrlsChange])

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(timer => {
        clearTimeout(timer)
      })
    }
  }, [])

  // Notify parent of media changes whenever images or videos change
  // biome-ignore lint/correctness/useExhaustiveDependencies: notifyMediaChange is stable and doesn't need to be in deps
  useEffect(() => {
    notifyMediaChange(images, videos)
  }, [images, videos])

  // Track blob URLs created for cleanup on unmount only
  const imageBlobUrlsRef = useRef<string[]>([])

  // Sync images from props when component mounts or props change
  // biome-ignore lint/correctness/useExhaustiveDependencies: images.some used for conditional sync check
  useEffect(() => {
    const imageArray = initialImages || []
    if (imageArray.length > 0) {
      // Check if we need to sync (avoid unnecessary re-renders)
      const hasAllImages = imageArray.every(img => images.some(i => i.id === img.id))

      if (!hasAllImages) {
        const syncedImages: ImageItem[] = []
        for (const img of imageArray) {
          // If we have a file, always create a fresh blob URL (img.src might be revoked/invalid)
          // Otherwise use src directly (server URL when no file available)
          if (img.file) {
            const url = URL.createObjectURL(img.file)
            imageBlobUrlsRef.current.push(url)
            syncedImages.push({
              id: img.id,
              src: url,
              file: img.file,
              isCover: img.isCover,
            })
          } else if (img.src) {
            syncedImages.push({
              id: img.id,
              src: img.src,
              isCover: img.isCover,
              uploadId: img.uploadId,
            })
          }
        }
        setImages(syncedImages)

        // Notify parent of loaded images so validation sees them
        if (onMediaChange) {
          onMediaChange({
            images: syncedImages.map(img => ({
              id: img.id,
              src: img.src,
              file: img.file,
              isCover: img.isCover,
            })),
            videos: videos.map(v => ({ id: v.id, url: v.url, file: v.file })),
          })
        }
      }
    }
  }, [initialImages])

  // Cleanup image blob URLs on unmount only
  useEffect(() => {
    return () => {
      for (const url of imageBlobUrlsRef.current) {
        URL.revokeObjectURL(url)
      }
    }
  }, [])

  // Track video blob URLs created for cleanup on unmount only
  const videoBlobUrlsRef = useRef<string[]>([])

  // Sync videos from props when component mounts or props change
  // biome-ignore lint/correctness/useExhaustiveDependencies: videos.some used for conditional sync check
  useEffect(() => {
    if (initialVideos && initialVideos.length > 0) {
      // Check if we need to sync (avoid unnecessary re-renders)
      const hasAllVideos = initialVideos.every(video => videos.some(v => v.id === video.id))
      if (!hasAllVideos) {
        const syncedVideos: VideoItem[] = []
        for (const video of initialVideos) {
          if (video.url) {
            // Existing video with URL
            syncedVideos.push({
              id: video.id,
              title: video.url.split("/").pop() || "video",
              url: video.url,
            })
          } else if (video.file) {
            // New video file
            const url = URL.createObjectURL(video.file)
            videoBlobUrlsRef.current.push(url)
            syncedVideos.push({
              id: video.id,
              title: video.file.name,
              url: url,
              file: video.file,
            })
          }
        }
        setVideos(syncedVideos)
      }
    }
  }, [initialVideos])

  // Cleanup video blob URLs on unmount only (combined with existing cleanup)
  useEffect(() => {
    return () => {
      for (const url of videoBlobUrlsRef.current) {
        URL.revokeObjectURL(url)
      }
    }
  }, [])

  const handleSetCover = (imageId: string) => {
    const updatedImages = images.map(img => ({
      ...img,
      isCover: img.id === imageId,
    }))
    setImages(updatedImages)
  }

  const handleDeleteImage = (imageId: string) => {
    const updatedImages = images.filter(img => img.id !== imageId)
    setImages(updatedImages)
    // Clear error when user deletes an image (gives them another chance to upload)
    setUploadError(null)
  }

  const aiGenerationIdRef = useRef<string | null>(null)
  const aiGenerationImageIndexRef = useRef<number | null>(null)

  const handleAiImageSelected = (file: File, generationId: string, imageIndex: number) => {
    if (images.length >= maxFiles) {
      setUploadError(`Maximum ${maxFiles} files allowed`)
      return
    }
    aiGenerationIdRef.current = generationId
    aiGenerationImageIndexRef.current = imageIndex
    const url = URL.createObjectURL(file)
    imageBlobUrlsRef.current.push(url)
    setImages(prev => {
      const newImage: ImageItem = {
        id: `ai-${Date.now()}-${Math.random()}`,
        src: url,
        file,
        isCover: prev.length === 0,
        aiGenerationImageIndex: imageIndex,
      }
      return [...prev, newImage]
    })
  }

  const notifyMediaChange = (currentImages: ImageItem[], currentVideos: VideoItem[]) => {
    if (onMediaChange) {
      const imageData = currentImages.map(img => ({
        id: img.id,
        file: img.file,
        src: img.src,
        isCover: img.isCover,
        uploadId: img.uploadId,
      }))

      const videoData = currentVideos.map(video => ({
        id: video.id,
        file: video.file,
        url: video.url,
      }))

      onMediaChange({
        images: imageData,
        videos: videoData,
        aiGenerationId: aiGenerationIdRef.current,
        aiGenerationImageIndex: aiGenerationImageIndexRef.current,
      })
    }
  }

  const handleModalUpload = async (files: File[]) => {
    const newImages: ImageItem[] = []
    const newAudios: AudioItem[] = []

    for (const file of files) {
      if (file.type.startsWith("audio/")) {
        // Same cap as the inline file-input path. Counted independently
        // per kind to match the existing image/video flow.
        if (audios.length + newAudios.length >= maxFiles) {
          setUploadError(`Maximum ${maxFiles} audio files allowed`)
          continue
        }
        newAudios.push({
          id: `${Date.now()}-${Math.random()}`,
          name: file.name,
          sizeBytes: file.size,
          file,
        })
        continue
      }
      // Default: treat anything else passed through validation as an image.
      if (images.length + newImages.length >= maxFiles) {
        setUploadError(`Maximum ${maxFiles} files allowed`)
        continue
      }
      const url = URL.createObjectURL(file)
      imageBlobUrlsRef.current.push(url)
      const newImage: ImageItem = {
        id: `${Date.now()}-${Math.random()}`,
        src: url,
        file,
        isCover: images.length + newImages.length === 0,
      }
      newImages.push(newImage)
    }

    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages])
    }
    if (newAudios.length > 0) {
      setAudios(prev => [...prev, ...newAudios])
    }

    if (onMediaChange) {
      onMediaChange({
        images: [...images, ...newImages],
        videos: videos.map(v => ({ id: v.id, url: v.url, file: v.file })),
        audios: toAudioPayload([...audios, ...newAudios]),
      })
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    // Clear previous error
    setUploadError(null)

    const filesArray = Array.from(files)

    filesArray.forEach(file => {
      // Check file type against allowedTypes
      const isAllowedType = allowedTypes.some(type => {
        if (type.startsWith("image/")) {
          return file.type.startsWith("image/")
        }
        if (type.startsWith("video/")) {
          return file.type.startsWith("video/")
        }
        if (type.startsWith("audio/")) {
          return file.type.startsWith("audio/")
        }
        return file.type === type
      })

      // Set error for invalid file types
      if (!isAllowedType) {
        const allowedExts = allowedTypes
          .map(t => t.replace("image/", "").replace("video/", "").replace("audio/", ""))
          .join(", ")
        setUploadError(`Invalid file type: ${file.type || "unknown"}. Allowed: ${allowedExts}`)
        return
      }

      // Per-class size enforcement: audio files use the larger audio cap;
      // images and videos use the standard maxFileSize.
      const cap = file.type.startsWith("audio/") ? maxAudioFileSize : maxFileSize
      if (file.size > cap) {
        const maxSizeMB = cap / (1024 * 1024)
        setUploadError(`File "${file.name}" exceeds maximum size of ${maxSizeMB}MB`)
        return
      }

      if (file.type.startsWith("image/")) {
        const reader = new FileReader()
        reader.onload = event => {
          setImages(prev => {
            if (prev.length >= maxFiles) {
              setUploadError(`Maximum ${maxFiles} files allowed`)
              return prev
            }
            const newImage: ImageItem = {
              id: `${Date.now()}-${Math.random()}`,
              src: event.target?.result as string,
              file,
              isCover: prev.length === 0,
            }
            return [...prev, newImage]
          })
        }
        reader.readAsDataURL(file)
      } else if (file.type.startsWith("video/")) {
        setVideos(prev => {
          if (prev.length >= maxFiles) {
            setUploadError(`Maximum ${maxFiles} files allowed`)
            return prev
          }
          const newVideo: VideoItem = {
            id: `${Date.now()}-${Math.random()}`,
            title: file.name,
            url: URL.createObjectURL(file),
            file,
          }
          return [...prev, newVideo]
        })
      } else if (file.type.startsWith("audio/")) {
        setAudios(prev => {
          if (prev.length >= maxFiles) {
            setUploadError(`Maximum ${maxFiles} files allowed`)
            return prev
          }
          const newAudio: AudioItem = {
            id: `${Date.now()}-${Math.random()}`,
            name: file.name,
            sizeBytes: file.size,
            file,
          }
          const next = [...prev, newAudio]
          notifyAudioChange(next)
          return next
        })
      }
    })

    // Reset input
    if (imageInputRef.current) {
      imageInputRef.current.value = ""
    }
  }

  // Build the audio-shape payload for onMediaChange. Single helper so every
  // mutation site (add, modal-add, poster-set, delete) emits the same shape.
  const toAudioPayload = (list: AudioItem[]) =>
    list.map(a => ({
      id: a.id,
      file: a.file,
      name: a.name,
      sizeBytes: a.sizeBytes,
      uploadId: a.uploadId,
      posterFile: a.posterFile,
      posterUrl: a.posterUrl,
    }))

  const notifyAudioChange = (next: AudioItem[]) => {
    if (!onMediaChange) return
    onMediaChange({
      images,
      videos: videos.map(v => ({ id: v.id, url: v.url, file: v.file })),
      audios: toAudioPayload(next),
    })
  }

  const handleDeleteAudio = (id: string) => {
    setAudios(prev => {
      const next = prev.filter(a => a.id !== id)
      const removed = prev.find(a => a.id === id)
      if (removed?.posterUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.posterUrl)
      }
      notifyAudioChange(next)
      return next
    })
  }

  const handleAudioPosterSelect = (audioId: string) => {
    if (!audioPosterInputRef.current) return
    audioPosterInputRef.current.value = ""
    ;(
      audioPosterInputRef.current as HTMLInputElement & { dataset: { audioId?: string } }
    ).dataset.audioId = audioId
    audioPosterInputRef.current.click()
  }

  const handleAudioPosterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const audioId = (e.target as HTMLInputElement & { dataset: { audioId?: string } }).dataset
      .audioId
    if (!file || !audioId) return

    if (!file.type.startsWith("image/")) {
      setThumbnailError("Audio cover must be an image (JPEG, PNG, GIF, WebP)")
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setThumbnailError("Audio cover must be less than 5MB")
      return
    }
    setThumbnailError(null)

    const previewUrl = URL.createObjectURL(file)
    setAudios(prev => {
      const next = prev.map(a => {
        if (a.id !== audioId) return a
        // Free the previous blob URL if any to avoid leaking object URLs.
        if (a.posterUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(a.posterUrl)
        }
        return { ...a, posterFile: file, posterUrl: previewUrl }
      })
      notifyAudioChange(next)
      return next
    })

    // Reset input so the same file can be re-picked later.
    if (audioPosterInputRef.current) {
      audioPosterInputRef.current.value = ""
    }
  }

  latestVideoUrlsRef.current = videoUrls

  return (
    <div className="space-y-7.5 mb-7.5">
      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept={allowedTypes.join(",")}
        multiple
        onChange={handleFileUpload}
        className="hidden"
      />
      <input
        ref={thumbnailInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleThumbnailChange}
        className="hidden"
      />
      <input
        ref={audioPosterInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleAudioPosterChange}
        className="hidden"
      />
      <h3 className="font-bold text-primary mb-3.75">Images</h3>
      <div className="flex items-center gap-5">
        <button
          type="button"
          onClick={() => setUploadModalOpen(true)}
          className="flex justify-between items-center w-full border border-dashed border-brand-slate-alpha-70 rounded-[15px] p-2.5 pr-5 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex justify-center items-center gap-2">
            <div className="bg-brand-slate-alpha-70 p-2.5 flex justify-center items-center rounded-full">
              <ArrowUpToLine className="w-5 h-5 text-white" />
            </div>
            <p className="font-semibold text-primary text-sm">Upload Media</p>
          </div>

          <p className="text-xs text-primary  leading-5">
            {showAudioSection
              ? `Up to ${maxFiles} files. PNG, JPG up to ${maxFileSize / (1024 * 1024)}MB; MP3 up to ${maxAudioFileSize / (1024 * 1024)}MB`
              : `Up to ${maxFiles} files. PNG, JPG up to ${maxFileSize / (1024 * 1024)}MB`}
          </p>
        </button>

        {/* Generate with AI Button */}
        {enableAiGeneration && aiEntityType && (
          <button
            type="button"
            onClick={() => setAiGeneratorOpen(true)}
            className="flex items-center gap-2.5 border border-brand-slate-alpha-70 rounded-full px-6.75 py-3 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors"
          >
            <Sparkles className="w-6 h-6 text-white" />
            <p className="font-semibold text-primary text-sm uppercase text-nowrap">
              Generate Image
            </p>
          </button>
        )}
      </div>
      {/* Error Message */}
      {uploadError && <p className="text-xs text-brand-terracotta-bright mt-1">{uploadError}</p>}
      {thumbnailError && (
        <p className="text-xs text-brand-terracotta-bright mt-1">{thumbnailError}</p>
      )}
      {/* Images Section */}
      <div className="mt-4">
        {images.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {images.map(image => (
              <div key={image.id} data-image-id={image.id} data-image-src={image.src}>
                <MediaPreview
                  type="image"
                  src={image.src}
                  isCover={image.isCover}
                  onCoverSelect={() => handleSetCover(image.id)}
                  onRemove={() => handleDeleteImage(image.id)}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* AI Image Generator Dialog */}
      {enableAiGeneration && aiEntityType && (
        <AiImageGenerator
          open={aiGeneratorOpen}
          onOpenChange={setAiGeneratorOpen}
          entityType={aiEntityType}
          entityContext={aiEntityContext}
          onImageSelected={handleAiImageSelected}
        />
      )}

      {/* Videos Section - Only render if showVideoSection is true (default) */}
      {showVideoSection && showVideoUrlInput && (
        <div className="mt-3">
          <h3 className="font-bold text-pimary mb-5">Videos</h3>

          {/* Video URL Inputs */}
          {showVideoUrlInput && (
            <div className="space-y-5">
              {/* Video entries */}
              {videoUrls.map((video, index) => {
                // Show input when: URL is empty OR editing OR (URL exists but not processed yet) OR URL is invalid
                const showInput =
                  !video.url || video.isEditing || !video.isProcessed || video.isInvalid
                // Show thumbnail when: URL exists AND is processed AND not editing AND not invalid
                const showThumbnail =
                  video.url && video.isProcessed && !video.isEditing && !video.isInvalid

                return (
                  <div
                    key={video.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={e => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-start gap-2 ${draggedIndex === index ? "opacity-50" : ""}`}
                  >
                    {/* Drag Handle - always show */}
                    <div
                      className="mt-4 p-2 text-body cursor-grab active:cursor-grabbing hover:text-primary transition-colors"
                      title="Drag to reorder"
                    >
                      <GripVertical className="w-5 h-5" />
                    </div>

                    {/* Input View - show when URL is empty or editing */}
                    {showInput && (
                      <>
                        <div className="flex-1">
                          <CustomInput
                            id={`videoUrl-${index}`}
                            label={"Embed YouTube or Vimeo video"}
                            value={video.url}
                            onChange={e => handleVideoUrlChange(e, index)}
                            onBlur={() => handleVideoUrlBlur(index)}
                            placeholder="Enter YouTube or Vimeo URL"
                            error={urlErrors[index] || undefined}
                            onClearError={() => setUrlErrors(prev => ({ ...prev, [index]: null }))}
                          />
                        </div>
                        {/* Show trash icon only if not first empty input */}
                        {(index > 0 || video.url) && (
                          <button
                            type="button"
                            onClick={() => handleDeleteVideo(index)}
                            className="mt-6 p-2 text-white cursor-pointer"
                            title="Remove video"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </>
                    )}

                    {/* Thumbnail View - show when URL exists and not editing */}
                    {showThumbnail && (
                      <>
                        {/* Thumbnail or Placeholder */}
                        <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-base flex items-center justify-center">
                          {video.thumbnail ? (
                            <img
                              src={video.thumbnail}
                              alt="Video thumbnail"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Video className="w-10 h-10 text-body" />
                          )}
                        </div>
                        {/* Video info */}
                        <div className="flex-1 min-w-0 py-1 h-[75px] flex flex-col items-start justify-center">
                          {video.title ? (
                            <p className="text-sm font-medium text-primary truncate overflow-hidden md:w-[500px] w-[200px]">
                              {video.title}
                            </p>
                          ) : null}
                          <p className="text-xs text-body truncate overflow-hidden md:w-[500px] w-[200px]">
                            {video.url}
                          </p>
                        </div>
                        {/* Edit button */}
                        <button
                          type="button"
                          onClick={() => handleEditVideo(index)}
                          className="mt-4 p-2 text-primary hover:text-primary/80 cursor-pointer"
                          title="Edit video"
                        >
                          <Pencil className="w-4.5 h-4.5" />
                        </button>
                        {/* Thumbnail upload button - between Edit and Delete */}
                        <button
                          type="button"
                          onClick={() => handleThumbnailSelect(index)}
                          className="mt-4 p-2 text-primary hover:text-primary/80 cursor-pointer relative"
                          title="Upload custom thumbnail"
                          disabled={uploadingThumbnailIndex === index}
                        >
                          {uploadingThumbnailIndex === index ? (
                            <div className="w-4.5 h-4.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Image className="w-4.5 h-4.5" />
                          )}
                        </button>
                        {/* Delete button */}
                        <button
                          type="button"
                          onClick={() => handleDeleteVideo(index)}
                          className="mt-4 p-2 text-white cursor-pointer"
                          title="Remove video"
                        >
                          <Trash2 className="w-4.5 h-4.5" />
                        </button>
                      </>
                    )}
                  </div>
                )
              })}

              {/* + Add More Videos Button */}
              <button
                type="button"
                onClick={addVideoUrl}
                className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium"
              >
                <Plus className="w-5 h-5" />
                Add More
              </button>
            </div>
          )}
        </div>
      )}

      {/* Audio section — only rendered when the parent enables audio uploads.
          Audio rows are uploaded via the same hidden file input as images
          (handleFileUpload routes by MIME). They never participate in the
          cover-image selection. */}
      {showAudioSection && audios.length > 0 && (
        <div className="mt-3">
          <h3 className="font-bold text-primary mb-5">Audio</h3>
          <div className="space-y-5">
            {audios.map(audio => (
              <div key={audio.id} className="flex items-start gap-2">
                <div
                  className="mt-4 p-2 text-body cursor-grab active:cursor-grabbing hover:text-primary transition-colors"
                  title="Drag to reorder"
                >
                  <GripVertical className="w-5 h-5" />
                </div>
                {/* Thumbnail tile: shows the user-uploaded poster image when
                    set, otherwise the music-note placeholder. Mirrors the
                    custom-thumbnail affordance on video URL rows. */}
                <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-base flex items-center justify-center">
                  {audio.posterUrl ? (
                    <img
                      src={audio.posterUrl}
                      alt="Audio cover"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Music className="w-10 h-10 text-body" />
                  )}
                </div>
                <div className="flex-1 min-w-0 py-1 h-[75px] flex flex-col items-start justify-center">
                  <p className="text-sm font-medium text-primary truncate overflow-hidden md:w-[500px] w-[200px]">
                    {audio.name}
                  </p>
                  <p className="text-xs text-body">
                    {audio.file?.type === "audio/mpeg" || audio.file?.type === "audio/mp3"
                      ? "MP3"
                      : "Audio"}
                    , {formatFileSize(audio.sizeBytes)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleAudioPosterSelect(audio.id)}
                  className="mt-4 p-2 text-primary hover:text-primary/80 cursor-pointer"
                  title={audio.posterUrl ? "Replace cover image" : "Upload cover image"}
                >
                  <Image className="w-4.5 h-4.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteAudio(audio.id)}
                  className="mt-4 p-2 text-white cursor-pointer"
                  title="Remove audio"
                >
                  <Trash2 className="w-4.5 h-4.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <MediaUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onUpload={handleModalUpload}
        maxFiles={maxFiles}
        maxFileSize={maxFileSize}
        allowedTypes={allowedTypes}
        title="Upload Media"
        existingFilesCount={images.length}
      />
    </div>
  )
}
